import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AuthService } from '../../../core/auth/auth.service';
import { ChatApiService } from './chat-api.service';
import { ChatSoundService } from './chat-sound.service';
import { ChatBadge, ChatConversation, ChatMessage, ChatNotification, NOTIFICATIONS_CONVERSATION_ID } from '../models/chat.model';
import { setFaviconBadge } from '../utils/favicon-badge';
import { notify, requestPermissionOnce } from '../utils/browser-notify';

export type ChatWidgetView = 'closed' | 'list' | 'conversation' | 'notifications';

// Odpytywanie (Patryk 28.09.2026 - "zrobmy... a zobaczymy"): 30s gdy widget zamkniety/lista,
// 5s gdy user aktywnie patrzy na konkretny czat. WebSocket odlozony na pozniej.
const BADGE_POLL_MS = 30000;
const CONVERSATION_POLL_MS = 5000;

@Injectable({ providedIn: 'root' })
export class ChatStateService {
  readonly view$ = new BehaviorSubject<ChatWidgetView>('closed');
  readonly activeConversationId$ = new BehaviorSubject<number | null>(null);
  readonly badge$ = new BehaviorSubject<ChatBadge>({ total: 0, chats: 0, notifications: 0, pendingAck: 0 });
  readonly conversations$ = new BehaviorSubject<ChatConversation[]>([]);
  readonly messages$ = new BehaviorSubject<ChatMessage[]>([]);
  readonly notifications$ = new BehaviorSubject<ChatNotification[]>([]);
  /** Domyslnie ukrywa rozmowy z nieaktywnymi (bylymi) pracownikami - checkbox na liscie
   *  odkrywa je z powrotem (29.09.2026, zastapil rezne ukrywanie per-rozmowa). */
  readonly showInactive$ = new BehaviorSubject<boolean>(false);

  /** Id wiadomosci/powiadomien "swiezych" w BIEZACYM otwarciu tego konkretnego czatu - zamrozone
   *  w chwili otwarcia, zeby linia/ramka "nowe" nie przeskakiwala w trakcie czytania. */
  readonly freshIds$ = new BehaviorSubject<Set<number>>(new Set());

  private started = false;
  private badgeSub?: Subscription;
  private conversationSub?: Subscription;
  private lastBadgeTotal = 0;
  private myProfilId: number | null = null;

  constructor(
    private readonly api: ChatApiService,
    private readonly auth: AuthService,
    private readonly snackBar: MatSnackBar,
    private readonly translate: TranslateService,
    private readonly sound: ChatSoundService,
  ) {}

  // Akcje inicjowane przez usera (utworzenie rozmowy, wyslanie wiadomosci itd.) dostaja
  // widoczny komunikat przy bledzie - do 28.09.2026 kazdy blad byl polykany po cichu
  // (error: () => undefined), co przy np. zlym apiBaseUrl (patrz ConfigService) dawalo
  // "nic sie nie dzieje" bez zadnej wskazowki, co poszlo nie tak (zgloszenie Patryka:
  // "nie udalo mi sie zalozyc chata"). Bledy samego, cichego pollingu w tle (badge,
  // odswiezanie listy, poll otwartej rozmowy) CELOWO zostaja ciche - inaczej padly
  // lokalny/produkcyjny backend zasypywalby usera tym samym komunikatem co 5-30s.
  private showError(err: unknown): void {
    const message = (err as { error?: { message?: string } })?.error?.message
      ?? this.translate.instant('CHAT.ACTION_FAILED');
    this.snackBar.open(message, undefined, { duration: 5000 });
  }

  init(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.myProfilId = this.auth.getCurrentUserId();

    this.badgeSub = timer(1500, BADGE_POLL_MS)
      .pipe(switchMap(() => this.api.badge()))
      .subscribe({
        next: (badge) => {
          this.badge$.next(badge);
          setFaviconBadge(badge.total);

          // badge.total > lastBadgeTotal = REALNY przyrost (nowa wiadomosc/powiadomienie),
          // nie sam fakt, ze cos jest nadal nieprzeczytane - inaczej dzwiek/powiadomienie
          // odpalalyby sie na kazdym 30s tiku, dopoki user nie przeczyta (Patryk 29.09.2026).
          if (badge.total > this.lastBadgeTotal) {
            notify('Nowa wiadomość', 'Masz nowe, nieprzeczytane wiadomości lub powiadomienia.');
            this.sound.play();
          }

          this.lastBadgeTotal = badge.total;

          // Widget zamkniety/lista - liczniki per-czat na liscie tez maja byc na biezaco.
          if (this.view$.value !== 'conversation') {
            this.refreshConversations();
          }
        },
        error: () => undefined,
      });
  }

  get profilId(): number | null {
    return this.myProfilId;
  }

  // ── Stany okienka ────────────────────────────────────────────────────

  toggleWidget(): void {
    if (this.view$.value === 'closed') {
      this.open();
    } else {
      this.collapseToClosed();
    }
  }

  open(): void {
    if (!this.auth.isLoggedIn()) {
      return;
    }

    requestPermissionOnce();
    this.view$.next('list');
    this.refreshConversations();
  }

  collapseToClosed(): void {
    this.stopConversationPolling();
    this.activeConversationId$.next(null);
    this.view$.next('closed');
  }

  closeConversation(): void {
    this.stopConversationPolling();
    this.activeConversationId$.next(null);
    this.view$.next('list');
    this.refreshConversations();
  }

  refreshConversations(): void {
    this.api.conversations(this.showInactive$.value).subscribe({
      next: (list) => this.conversations$.next(list),
      error: () => undefined,
    });
  }

  toggleShowInactive(): void {
    this.showInactive$.next(!this.showInactive$.value);
    this.refreshConversations();
  }

  // ── Powiadomienia (dzwonek - osobna ikonka/panel, nie czesc listy czatow) ──

  toggleNotifications(): void {
    if (this.view$.value === 'notifications') {
      this.closeNotifications();
    } else {
      this.openNotifications();
    }
  }

  openNotifications(): void {
    if (!this.auth.isLoggedIn()) {
      return;
    }

    requestPermissionOnce();
    this.stopConversationPolling();
    this.activeConversationId$.next(NOTIFICATIONS_CONVERSATION_ID);
    this.view$.next('notifications');
    this.loadNotifications(true);
    this.conversationSub = timer(CONVERSATION_POLL_MS, CONVERSATION_POLL_MS)
      .pipe(switchMap(() => (document.visibilityState === 'visible' ? this.api.notifications(50) : [])))
      .subscribe({
        next: (res) => { if (res) this.notifications$.next(res.items ?? []); },
        error: () => undefined,
      });
  }

  closeNotifications(): void {
    this.stopConversationPolling();
    this.activeConversationId$.next(null);
    this.view$.next('closed');
  }

  // ── Otwarcie konkretnego czatu ───────────────────────────────────────

  selectConversation(id: number): void {
    this.stopConversationPolling();
    this.activeConversationId$.next(id);
    this.view$.next('conversation');

    this.loadMessages(id, true);
    this.conversationSub = timer(CONVERSATION_POLL_MS, CONVERSATION_POLL_MS)
      .pipe(switchMap(() => (document.visibilityState === 'visible' ? this.api.messages(id) : [])))
      .subscribe({
        next: (msgs) => {
          if (!msgs) {
            return;
          }
          this.messages$.next(msgs);
          this.api.markConversationRead(id).subscribe({ next: () => this.refreshBadgeOnly(), error: () => undefined });
        },
        error: () => undefined,
      });
  }

  private stopConversationPolling(): void {
    this.conversationSub?.unsubscribe();
    this.conversationSub = undefined;
  }

  private loadMessages(conversationId: number, isFreshOpen: boolean): void {
    const conv = this.conversations$.value.find((c) => c.id === conversationId);
    const freshCount = isFreshOpen ? conv?.unreadCount ?? 0 : 0;

    this.api.messages(conversationId).subscribe({
      next: (msgs) => {
        this.messages$.next(msgs);

        if (isFreshOpen) {
          // Ostatnie `freshCount` wiadomosci (chronologicznie) = te, ktore byly nieprzeczytane
          // W CHWILI otwarcia - zamrazamy ten zbior, zeby przeglądanie nie przesuwalo linii.
          const fresh = freshCount > 0 ? msgs.slice(Math.max(0, msgs.length - freshCount)) : [];
          this.freshIds$.next(new Set(fresh.map((m) => m.id)));
        }

        // Bez tego badge (ikonka dzwonka/chatu w rogu) zostawal ze stara liczba az do
        // nastepnego 30s pollu, mimo ze user WLASNIE przeczytal wiadomosc w otwartym oknie
        // (zgloszenie Patryka 29.09.2026: "odczytalem wiadomosc, a po zamknieciu okienka na
        // ikonce nadal bylo 1").
        this.api.markConversationRead(conversationId).subscribe({ next: () => this.refreshBadgeOnly(), error: () => undefined });
      },
      error: () => undefined,
    });
  }

  private loadNotifications(isFreshOpen: boolean): void {
    this.api.notifications(50).subscribe({
      next: (res) => {
        const items = res.items ?? [];

        if (isFreshOpen) {
          this.freshIds$.next(new Set(items.filter((n) => !n.readAt).map((n) => n.id)));
          // Zwykle powiadomienia -> "przeczytane" przez samo otwarcie okienka (patrz plan
          // 28.09.2026). Specjalne (requiresAck) dostaja tez readAt (spojnosc), ale ZOSTAJA
          // nieobsluzone az do wlasnego, jawnego potwierdzenia - patrz acknowledgeNotification().
          this.api.readAllNotifications().subscribe({
            next: () => this.refreshBadgeOnly(),
            error: () => undefined,
          });
        }

        // Renderujemy z ZAMROZONYM stanem readAt sprzed oznaczenia - inaczej wszystko
        // odswiezoneby sie na "przeczytane" natychmiast i freshIds$ nie mialby juz sensu.
        this.notifications$.next(items);
      },
      error: () => undefined,
    });
  }

  acknowledgeNotification(id: number): void {
    this.api.acknowledgeNotification(id).subscribe({
      next: ({ ok }) => {
        if (!ok) {
          return;
        }

        const now = new Date().toISOString();
        this.notifications$.next(this.notifications$.value.map((n) => (n.id === id ? { ...n, ackAt: now, readAt: n.readAt ?? now } : n)));
        this.freshIds$.next(new Set([...this.freshIds$.value].filter((x) => x !== id)));
        this.refreshBadgeOnly();
      },
      error: (err) => this.showError(err),
    });
  }

  private refreshBadgeOnly(): void {
    this.api.badge().subscribe({
      next: (badge) => { this.badge$.next(badge); setFaviconBadge(badge.total); this.lastBadgeTotal = badge.total; },
      error: () => undefined,
    });
  }

  // ── Wiadomosci ───────────────────────────────────────────────────────

  sendMessage(conversationId: number, body: string): void {
    const trimmed = body.trim();

    if (!trimmed) {
      return;
    }

    this.api.sendMessage(conversationId, trimmed).subscribe({
      next: (msg) => this.messages$.next([...this.messages$.value, msg]),
      error: (err) => this.showError(err),
    });
  }

  editMessage(messageId: number, body: string, onDone?: (ok: boolean, error?: string) => void): void {
    this.api.editMessage(messageId, body).subscribe({
      next: (msg) => {
        this.messages$.next(this.messages$.value.map((m) => (m.id === messageId ? msg : m)));
        onDone?.(true);
      },
      error: (err) => { this.showError(err); onDone?.(false, err?.error?.message); },
    });
  }

  deleteMessage(messageId: number, onDone?: (ok: boolean, error?: string) => void): void {
    this.api.deleteMessage(messageId).subscribe({
      next: () => {
        this.messages$.next(this.messages$.value.map((m) => (m.id === messageId ? { ...m, deleted: true, body: null, editable: false, deletable: false } : m)));
        onDone?.(true);
      },
      error: (err) => { this.showError(err); onDone?.(false, err?.error?.message); },
    });
  }

  // ── Rozmowy ──────────────────────────────────────────────────────────

  createDirectAndOpen(profilId: number, onDone?: (conv: ChatConversation) => void): void {
    this.api.createDirect(profilId).subscribe({
      next: (conv) => {
        this.refreshConversations();
        this.selectConversation(conv.id);
        onDone?.(conv);
      },
      error: (err) => this.showError(err),
    });
  }

  createGroupAndOpen(name: string, profilIds: number[], onDone?: (conv: ChatConversation) => void): void {
    this.api.createGroup(name, profilIds).subscribe({
      next: (conv) => {
        this.refreshConversations();
        this.selectConversation(conv.id);
        onDone?.(conv);
      },
      error: (err) => this.showError(err),
    });
  }

  addParticipant(conversationId: number, profilId: number, showFullHistory: boolean, onDone?: (ok: boolean, error?: string) => void): void {
    this.api.addParticipant(conversationId, profilId, showFullHistory).subscribe({
      next: () => { this.refreshConversations(); onDone?.(true); },
      error: (err) => { this.showError(err); onDone?.(false, err?.error?.message); },
    });
  }

}
